import { ChangeEvent, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './AddStudent.module.css';

import SimpleBar from 'simplebar-react';
import useSimpleBar from '../../../../hooks/useSimpleBar';
import { useFormContext } from 'react-hook-form';

import {
  studentApiSlice,
  useValidateEmailMutation,
} from '../../../../services/student';
import { Email, ValidatedEmail } from '../../../../types/classroom';

import Item from '../Item';
import {
  ACCOUNT_INACTIVE_MESSAGE,
  ADD,
  ADD_STUDENT_DESC,
  DELETE,
  INSERT_SHARE_USER,
  INSERT_SHARE_USER_DESC,
  MAX_ADDITIONAL_STUDENTS,
  MAX_ADDITIONAL_STUDENTS_EXCEEDED,
  NOT_SIGNEDUP_USER,
} from '../../../../constants';
import { checkEmailPattern } from '../../../../utils/util';
import { useAppSelector } from '../../../../hooks/useRedux';
import {
  selectMaxStudent,
  selectStudentAddCnt,
} from '../../../../features/classroomSlice';
import { selectUserEmail } from '../../../../features/authSlice';

interface AddStudent {
  onChange: (element: HTMLInputElement | null, isFilled: boolean) => void;
}
export default function AddStudent({ onChange }: AddStudent) {
  const cx = classNames.bind(styles);
  const [simplebarRef] = useSimpleBar();
  const { setValue } = useFormContext();

  const inputEmailRef = useRef<HTMLInputElement>(null);
  const [searchList, setSearchList] = useState<Email[]>([]);
  const [isShowSearchResult, setIsShowSearchResult] = useState(false);
  const [validatedEmailList, setValidatedEmailList] = useState<
    ValidatedEmail[]
  >([]);
  const [validatedUserEmailList, setValidatedUserEmailList] = useState<
    ValidatedEmail[]
  >([]);

  const [getSearchStudent] = studentApiSlice.useLazyGetSearchStudentQuery();
  const [validateEmail] = useValidateEmailMutation();

  const maxStudent = useAppSelector(selectMaxStudent);
  const addibleCnt = useAppSelector(selectStudentAddCnt);
  const account = useAppSelector(selectUserEmail);

  const getSearchResult = async ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    const selectionStart = target.selectionStart ?? 0;
    const beforeCommaIdx = value.lastIndexOf(',', selectionStart - 1);
    const afterCommaIdx = value.indexOf(',', selectionStart);
    const endIdx = afterCommaIdx === -1 ? value.length : afterCommaIdx;
    const searchText = value
      .trim()
      .substring(beforeCommaIdx + 1, endIdx)
      .trim();

    if (searchText) {
      try {
        const searchResult = await getSearchStudent(searchText).unwrap();
        setSearchList(searchResult);
        setIsShowSearchResult(!!searchResult.length);
      } catch (error) {
        console.error('Failed to get search result', error);
      }
    } else {
      setSearchList([]);
      setIsShowSearchResult(false);
    }
  };

  const addEmail = async () => {
    const emailInput = inputEmailRef.current as HTMLInputElement;
    const emailList = emailInput.value
      .split(',')
      .filter((email) => checkEmailPattern(email.trim()) && email !== account)
      .reduce(
        (acc: { email: string }[], cur) =>
          validatedEmailList.map(({ email }) => email).includes(cur.trim())
            ? acc
            : [...acc, { email: cur.trim() }],
        []
      );

    if (!emailList.length) return;

    try {
      const validatedList = await validateEmail(emailList).unwrap();
      const newValidatedEmailList = [...validatedEmailList, ...validatedList];
      const newUserValidatedEmailList = newValidatedEmailList
        .filter(({ userYn, dormantYn }) => userYn && !dormantYn)
        .map((email, i) => ({ ...email, isExceeded: addibleCnt <= i }));

      setValidatedEmailList(newValidatedEmailList);
      setValidatedUserEmailList(newUserValidatedEmailList);
      setValue(
        'list',
        newValidatedEmailList.reduce(
          (acc, { email, dormantYn }) =>
            dormantYn
              ? acc
              : [
                  ...acc,
                  {
                    email,
                  },
                ],
          []
        )
      );
      setIsShowSearchResult(false);
      onChange(null, addibleCnt - newUserValidatedEmailList.length >= 0);
      emailInput.value = '';
    } catch (error) {
      console.error('Failed to validate email', error);
    }
  };

  const removeEmail = (targetEmail: string) => {
    const newAdditionalList = validatedEmailList.filter(
      ({ email }) => email !== targetEmail
    );
    const newUserValidatedEmailList = newAdditionalList
      .filter(({ userYn, dormantYn }) => userYn && !dormantYn)
      .map((email, i) => ({ ...email, isExceeded: addibleCnt <= i }));

    setValidatedEmailList(newAdditionalList);
    setValidatedUserEmailList(newUserValidatedEmailList);
    setValue('list', newAdditionalList);
    onChange(
      null,
      !!newAdditionalList.length &&
        addibleCnt - newUserValidatedEmailList.length >= 0
    );
  };

  function Email({ alias, email, enableYn }: Email) {
    const emailRef = useRef<HTMLSpanElement>(null);
    const isAddedEmail = !!validatedEmailList.filter(
      ({ email: addedEmail }) => addedEmail === email
    ).length;

    const selectEmail = () => {
      const email = emailRef.current?.textContent as string;
      const emailInput = inputEmailRef.current as HTMLInputElement;
      const inputValue = emailInput.value;
      const additionalWaitingList = inputValue
        .split(',')
        .map((value) => value.trim());
      const selectionStart = emailInput.selectionStart ?? 0;
      const beforeCommaIdx = emailInput.value.lastIndexOf(
        ',',
        selectionStart - 1
      );

      if (!additionalWaitingList.includes(email)) {
        if (inputValue.length > selectionStart) {
          const afterCommaIdx = inputValue.indexOf(',', selectionStart);
          emailInput.value =
            inputValue.substring(0, beforeCommaIdx + 1) +
            `${beforeCommaIdx === -1 ? '' : ' '}${email}` +
            (afterCommaIdx === -1 ? '' : inputValue.substring(afterCommaIdx));
        } else {
          emailInput.value = `${inputValue.substring(0, beforeCommaIdx + 1)}${
            beforeCommaIdx === -1 ? '' : ' '
          }${email}`;
        }
      }

      setIsShowSearchResult(false);
    };

    return (
      <li
        className={cx({ 'is--disabled': !enableYn || isAddedEmail })}
        onClick={selectEmail}
      >
        <span className={cx('text_clip')} title={email} ref={emailRef}>
          {email}
        </span>
        <span className={cx('text_clip')} title={alias}>
          {alias}
        </span>
      </li>
    );
  }

  return (
    <Item id="addStudent">
      <div className={cx('panel_row', 'column')}>
        <div className={cx('insert_area')}>
          <div className={cx('insert_user')}>
            <input
              type="text"
              className={cx('input_t', 'line')}
              placeholder={INSERT_SHARE_USER}
              onChange={getSearchResult}
              ref={inputEmailRef}
            />
            <button
              type="button"
              className={cx('btn', 'b_t', 'dull')}
              onClick={addEmail}
            >
              <span>{ADD}</span>
            </button>
            <span className={cx('desc')}>{INSERT_SHARE_USER_DESC}</span>
            {isShowSearchResult && (
              <div className={cx('overlay_list_area')}>
                <SimpleBar forceVisible={true} ref={simplebarRef}>
                  <ul>
                    {searchList.map(({ alias, email, enableYn }) => (
                      <Email
                        key={email}
                        alias={alias}
                        email={email}
                        enableYn={enableYn}
                      />
                    ))}
                  </ul>
                </SimpleBar>
              </div>
            )}
          </div>
        </div>
        {validatedEmailList.length ? (
          <div className={cx('user_list')}>
            <SimpleBar forceVisible={true} ref={simplebarRef}>
              <ul>
                {validatedEmailList.map(({ email, userYn, dormantYn }) => {
                  const isExceededEmail = validatedUserEmailList.find(
                    ({ email: userEmail }) => userEmail === email
                  )?.isExceeded;

                  return (
                    <li key={email} className={cx({ notice: isExceededEmail })}>
                      <span
                        className={cx('text_clip', {
                          wrong: !userYn || dormantYn,
                        })}
                        title={
                          !userYn
                            ? NOT_SIGNEDUP_USER
                            : dormantYn
                            ? ACCOUNT_INACTIVE_MESSAGE
                            : isExceededEmail
                            ? MAX_ADDITIONAL_STUDENTS_EXCEEDED
                            : email
                        }
                      >
                        {email}
                      </span>
                      <span
                        role="button"
                        className={cx('btn', 'delete_user')}
                        onClick={() => removeEmail(email)}
                      >
                        {DELETE}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </SimpleBar>
          </div>
        ) : null}
        <p className={cx('over_guide')}>
          {MAX_ADDITIONAL_STUDENTS} {' : '}
          <span>{Math.max(0, addibleCnt - validatedUserEmailList.length)}</span>
        </p>
        <p className={cx('desc')}>{ADD_STUDENT_DESC}</p>
      </div>
    </Item>
  );
}
